// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { AdvisorPageContext, AdvisorPageType } from '../../types/pageContext';

/**
 * Compute a context-aware placeholder for Fin's input box. Two
 * dimensions, in priority order:
 *
 *   1. Question-aware: if the last assistant message ends with a
 *      question, scan it for known asks (email, profile name, phone,
 *      policy number, etc.) and hint the expected shape of the reply.
 *      Wins over page-aware because once Fin asks a specific question,
 *      the advisor is replying — not browsing the page.
 *
 *   2. Page-aware: based on the current advisor-ui page, suggest a
 *      typical opening prompt ("Summarise this relationship", "What's
 *      pending here?", etc.). Defaults to a generic Fin suggestion
 *      when no page context is registered.
 *
 * Pure function so it's trivial to unit-test.
 */
export function companionPlaceholder(opts: {
  pageContext?: AdvisorPageContext;
  lastAssistantText?: string;
}): string {
  const fromQuestion = questionAwarePlaceholder(opts.lastAssistantText);
  if (fromQuestion) return fromQuestion;
  return pageAwarePlaceholder(opts.pageContext?.pageType);
}

/**
 * Pattern-match the assistant's latest question against common asks
 * the propose_* tools need (email, name, phone, etc.) and return a
 * hint of the expected reply shape. Returns null when no pattern
 * matches — caller falls back to the page-aware default.
 *
 * Heuristics intentionally err on the side of recall over precision:
 * the worst case for a false positive is a slightly off-topic
 * placeholder, which is no worse than the generic fallback.
 */
function questionAwarePlaceholder(text?: string): string | null {
  if (!text) return null;
  // We only match when the assistant asked something — bail out fast
  // for short / non-question replies so we don't show a "what email"
  // hint after a normal answer.
  if (!text.includes('?')) return null;
  const t = text.toLowerCase();

  // Order matters: more specific patterns first.
  if (/email\s+address|client.?s?\s+email|what\s+email/.test(t)) {
    return 'e.g. john.dominic@example.com';
  }
  if (/profile\s+name|relationship\s+name|name\s+this\s+(profile|relationship)|call\s+(this\s+)?(profile|relationship)/.test(t)) {
    return 'e.g. Smith Pension Review';
  }
  if (/(profile\s+type|is\s+this\s+a\s+joint|individual\s+or\s+joint|solo,?\s+joint,?\s+or\s+group|joint\s+or\s+group)/.test(t)) {
    return 'Individual / Joint / Group';
  }
  if (/(first\s+name|given\s+name)/.test(t)) {
    return 'e.g. John';
  }
  if (/(last\s+name|surname|family\s+name)/.test(t)) {
    return 'e.g. Dominic';
  }
  if (/phone(\s+number)?|mobile/.test(t)) {
    return 'e.g. +353 87 1234567';
  }
  if (/policy\s+number|policy\s+ref/.test(t)) {
    return 'e.g. P-12345';
  }
  if (/which\s+provider|provider\s+name|provider\s+id/.test(t)) {
    return 'e.g. Aviva, Zurich, Irish Life…';
  }
  if (/premium(\s+amount)?/.test(t)) {
    return 'e.g. 1200.00';
  }
  if (/date\s+of\s+birth|dob|birthday/.test(t)) {
    return 'e.g. 29/05/1980';
  }
  if (/note\s+title|title\s+for\s+(this\s+)?note/.test(t)) {
    return 'e.g. Q1 review call';
  }
  // Generic-question fallback when we know Fin asked something but
  // can't pattern-match the topic. Better than the stale fund prompt.
  return 'Type your answer…';
}

/** Page-aware default when there's no active question. */
function pageAwarePlaceholder(pageType?: AdvisorPageType): string {
  switch (pageType) {
    case 'DASHBOARD':
      return "What's pending today? Summarise a relationship…";
    case 'RELATIONSHIPS_LIST':
      return 'Summarise a relationship, list pending requests…';
    case 'RELATIONSHIP_DETAIL':
      return "Summarise this relationship, what's pending here?";
    case 'CLIENT_DETAIL':
      return 'Latest note here? Add a note about today\'s call…';
    case 'POLICY_DETAIL':
      return 'Latest note on this policy, provider details…';
    case 'LOA_DETAIL':
      return "What's the status of this LOA?";
    case 'COA_DETAIL':
      return "What's the status of this CoA?";
    case 'REQUEST_DETAIL':
      return "What's the next action on this request?";
    case 'FUNDS':
    case 'FUND_DETAIL':
      return 'Ask about a fund, compare two, request a factsheet…';
    case 'STRATEGY_DETAIL':
      return 'Compare allocations, list components, performance…';
    case 'QUOTES':
    case 'QUOTE_DETAIL':
      return 'Compare options, generate a Statement of Suitability…';
    case 'TODAY':
    case 'CALENDAR':
      return "Who am I seeing today? Summarise tomorrow's meetings…";
    case 'ACTIVITY':
      return 'Summarise recent activity, find a specific event…';
    case 'SETTINGS':
      return 'Ask Fin about clients, policies, funds…';
    case 'UNKNOWN':
    default:
      return 'Ask Fin about clients, policies, funds…';
  }
}
